import { type PropsWithChildren, type ReactNode, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'

import PageHeader from '../../../components/ui/pageHeader/PageHeader'

const TOC = [
  { id: 'c1', n: '01' },
  { id: 'c2', n: '02' },
  { id: 'c3', n: '03' },
  { id: 'c4', n: '04' },
  { id: 'c5', n: '05' },
  { id: 'c6', n: '06' },
  { id: 'c7', n: '07' },
  { id: 'c8', n: '08' },
  { id: 'c9', n: '09' },
  { id: 'c10', n: '10' },
  { id: 'c11', n: '11' },
  { id: 'c12', n: '12' },
  { id: 'c13', n: '13' },
] as const

const EmailLink = ({ children }: PropsWithChildren) => (
  <a href="mailto:contact.padel.house@gmail.com">{children}</a>
)

const PhoneLink = ({ children }: PropsWithChildren) => (
  <a href="tel:0774158714">{children}</a>
)

const AuthorityLink = ({ children }: PropsWithChildren) => (
  <a
    href="https://www.dataprotection.ro"
    target="_blank"
    rel="noopener noreferrer"
  >
    {children}
  </a>
)

const Section = ({
  id,
  n,
  children,
}: {
  id: string
  n: string
  children: ReactNode
}) => {
  const { t } = useTranslation('terms')

  return (
    <section id={id} className="legal-section">
      <h2>
        <span className="legal-section-num">{n}</span>
        {t(`${id}.title`)}
      </h2>
      {children}
    </section>
  )
}

const Terms = () => {
  const { t } = useTranslation('terms')
  const { hash } = useLocation()

  useEffect(() => {
    const id = hash.replace('#', '')
    if (!id) {
      return
    }

    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }, [hash])

  return (
    <div className="legal-page">
      <PageHeader
        title={{
          eyebrow: t('eyebrow'),
          h1: t('title'),
        }}
        sub={t('lead')}
      />
      <div className="legal-meta-row">
        <span className="legal-badge">{t('terms:publishedBadge')}</span>
        <span className="legal-company">{t('terms:company')}</span>
      </div>

      <nav className="legal-toc" aria-label={t('terms:toc.label')}>
        <div className="eyebrow">{t('terms:toc.label')}</div>
        <div className="legal-toc-grid">
          {TOC.map(item => (
            <a key={item.id} href={`#${item.id}`}>
              <span className="legal-toc-num">{item.n}</span>
              <span>{t(`terms:${item.id}.title`)}</span>
            </a>
          ))}
        </div>
      </nav>

      <div className="legal-content">
        <Section id="c1" n="01">
          <p>
            <Trans i18nKey="terms:c1.p1" components={{ company: <strong /> }} />
          </p>
          <p className="legal-contact">
            <Trans
              i18nKey="terms:c1.p2"
              components={{
                label: <strong />,
                email: <EmailLink />,
                phone: <PhoneLink />,
              }}
            />
          </p>
        </Section>

        <Section id="c2" n="02">
          <p>{t('terms:c2.p1')}</p>
          <p>{t('terms:c2.p2')}</p>
        </Section>

        <Section id="c3" n="03">
          <p>{t('terms:c3.p1')}</p>
          <p>{t('terms:c3.p2')}</p>
          <p>{t('terms:c3.p3')}</p>
          <p>{t('terms:c3.p4')}</p>
        </Section>

        <Section id="c4" n="04">
          <p>{t('terms:c4.p1')}</p>
          <p>{t('terms:c4.p2')}</p>
        </Section>

        <Section id="c5" n="05">
          <p>{t('terms:c5.p1')}</p>
          <p>{t('terms:c5.p2')}</p>
          <p>{t('terms:c5.p3')}</p>
          <p>{t('terms:c5.p4')}</p>
          <p>{t('terms:c5.p5')}</p>
          <p>{t('terms:c5.p6')}</p>
          <p>{t('terms:c5.p7')}</p>
          <p>{t('terms:c5.p8')}</p>
        </Section>

        <Section id="c6" n="06">
          <p>{t('terms:c6.p1')}</p>
          <p>{t('terms:c6.p2')}</p>
          <p>{t('terms:c6.p3')}</p>
          <p>{t('terms:c6.p4')}</p>
        </Section>

        <Section id="c7" n="07">
          <p>{t('terms:c7.p1')}</p>
          <p>{t('terms:c7.p2')}</p>
          <ol className="legal-list">
            <li>{t('terms:c7.li1')}</li>
            <li>{t('terms:c7.li2')}</li>
          </ol>
          <p>{t('terms:c7.p3')}</p>
          <p>{t('terms:c7.p4')}</p>
          <p>{t('terms:c7.p5')}</p>
          <p>{t('terms:c7.p6')}</p>
          <p>{t('terms:c7.p7')}</p>
          <p>{t('terms:c7.p8')}</p>
          <p>{t('terms:c7.p9')}</p>
        </Section>

        <Section id="c8" n="08">
          <p>{t('terms:c8.p1')}</p>
          <p>{t('terms:c8.p2')}</p>
          <p>{t('terms:c8.p3')}</p>
          <p>{t('terms:c8.p4')}</p>
        </Section>

        <Section id="c9" n="09">
          <p>{t('terms:c9.p1')}</p>
          <p>{t('terms:c9.p2')}</p>
        </Section>

        <Section id="c10" n="10">
          <p>{t('terms:c10.p1')}</p>
          <p>{t('terms:c10.intro')}</p>
          <ul>
            <li>{t('terms:c10.li1')}</li>
            <li>{t('terms:c10.li2')}</li>
            <li>{t('terms:c10.li3')}</li>
          </ul>
          <p>{t('terms:c10.p2')}</p>
          <p>{t('terms:c10.p3')}</p>
          <p>
            <Trans
              i18nKey="terms:c10.p4"
              components={{
                email: <EmailLink />,
                authority: <AuthorityLink />,
              }}
            />
          </p>
        </Section>

        <Section id="c11" n="11">
          <p>{t('terms:c11.p1')}</p>
        </Section>

        <Section id="c12" n="12">
          <p>{t('terms:c12.p1')}</p>
          <p>{t('terms:c12.p2')}</p>
          <p>{t('terms:c12.p3')}</p>
        </Section>

        <Section id="c13" n="13">
          <p>
            <Trans
              i18nKey="terms:c13.p1"
              components={{
                email: <EmailLink />,
              }}
            />
          </p>
          <p>{t('terms:c13.p2')}</p>
          <p className="legal-meta">{t('terms:published')}</p>
        </Section>
      </div>
    </div>
  )
}

export default Terms
